import React, { useState } from 'react';
import Header from './Header';
import TimeSeriesCharts from './TimeSeriesCharts';
import DualYTimeSeriesChart from './Dual-Y-time-series-chart/DualYTimeSeriesChart';
import './dashboard.scss';
import MultiYTimeSeriesChart from './Multi-Y-time-series-chart/MultiYTimeSeriesChart';
import RealTimeDataTable from '../real-time-data/RealTimeData';
import DailyAverageData from '../daily-average-data/DailyAverage';
import { useAppSelector } from '../../hooks/storeHooks';
import COVTimeSeriesChart from './COV-time-series-chart/COVTimeSeriesChart';
import OilGasChart from '../oil-gas-liquid-chart/OilGasChart';
import DailyAverageChart from '../daily-average-chart/DailyAverageChart';
import SplitWellTestChart from '../../test-well-screen/split-well-test/SplitWellTest';
import AnalysisChart from '../analysis-chart/AnalysisChart';
import EmptyState from '../empty-state/EmptyState';
import { R, useRBAC, WIDGETS } from '../../rbac';
import { ProtectComponent, ProtectAction } from '../../rbac';

interface TimeSeriesDataProps {
  timeseriesChartData: any;
  openAddTaskModal: (status: true) => void;
  openAddNoteModal: (status: true) => void;
  layout: string;
  chartType?: string;
  accessible?: boolean;
  widgetName: string;
  externalChartRefs?: React.MutableRefObject<(any | null)[]>;
  onChartRegistered?: (index: number, chart: any) => void;
  className?: string;
}
const Dashboard: React.FC<TimeSeriesDataProps> = ({
  timeseriesChartData,
  openAddTaskModal,
  openAddNoteModal,
  layout,
  //chartType,
  accessible,
  widgetName,
  externalChartRefs,
  onChartRegistered,
  className,
}) => {
  const dashboardName = useAppSelector((state: any) => state.AssetAnalysis.chartType);
  const isTestDashboard = dashboardName?.toLowerCase().includes('tests');
  const isChartsDashboard = dashboardName?.toLowerCase().includes('charts');
  const [isEditYAxisOpen, setIsEditYAxisOpen] = useState(false);
  const [yAxisRefreshVersion, setYAxisRefreshVersion] = useState(0);

  const resource = R.widget(dashboardName, widgetName);
  const { can } = useRBAC();
  const rPermission = {
    canEdit: can(resource, 'edit'),
    canExportData: can(resource, 'exportData'),
    canExportDailyAverage: can(resource, 'exportDailyAverage'),
    canExportPdf: can(resource, 'exportPdf'),
    canExportJpg: can(resource, 'exportJpg'),
    canConfigureCharts: can(resource, 'configureCharts'),
    canRefreshData: can(resource, 'refreshData'),
    canAddWellNotes: can(resource, 'addWellNotes'),
    canAddTasks: can(resource, 'addTasks'),
    canViewOptions: can(resource, 'viewOptions'),
    canEditYAxis: can(resource, 'editYAxis'),
    canExpand: can(resource, 'expand'),
    canDailyAverageExpand: can(resource, 'dailyAverageExpand'),
    canDailyAverageExportPdf: can(resource, 'dailyAverageExportPdf'),
    canDailyAverageExportJpg: can(resource, 'dailyAverageExportJpg'),
    canAnalysisGraphExpand: can(resource, 'analysisGraphExpand'),
    canAnalysisGraphExportPdf: can(resource, 'analysisGraphExportPdf'),
    canAnalysisGraphExportJpg: can(resource, 'analysisGraphExportJpg'),
    canEnableStep: can(resource, 'enableStep'),
  };

  const layoutComponents: { [key: string]: JSX.Element } = {
    Stacked: (
      <ProtectComponent
        resource={resource}
        action='viewStackedCharts'
        showInWidget={true}
        widgetMessageName='Stacked charts'
      >
        <TimeSeriesCharts
          timeseriesChartData={timeseriesChartData}
          yAxisRefreshVersion={yAxisRefreshVersion}
          rPermission={rPermission}
          externalChartRefs={externalChartRefs}
          onChartRegistered={onChartRegistered}
        />
        ,
      </ProtectComponent>
    ),
    '2-Y': (
      <ProtectComponent resource={resource} action='view2YCharts' showInWidget={true} widgetMessageName='2-Y charts'>
        <DualYTimeSeriesChart timeseriesChartData={timeseriesChartData} />
      </ProtectComponent>
    ),
    'Multi-Y': (
      <ProtectComponent
        resource={resource}
        action='viewMultiYCharts'
        showInWidget={true}
        widgetMessageName='Multi-Y charts'
      >
        <MultiYTimeSeriesChart timeseriesChartData={timeseriesChartData} />
      </ProtectComponent>
    ),
    Data: (
      <ProtectComponent resource={resource} action='viewData' showInWidget={true} widgetMessageName='Data'>
        <RealTimeDataTable rPermission={rPermission} />
      </ProtectComponent>
    ),
    'Daily average': (
      <ProtectComponent
        resource={resource}
        action='viewDailyAverage'
        showInWidget={true}
        widgetMessageName='Daily average'
      >
        <DailyAverageData rPermission={rPermission} />
      </ProtectComponent>
    ),
    COV: (
      <ProtectComponent resource={resource} action='viewCovCharts' showInWidget={true} widgetMessageName='COV charts'>
        <COVTimeSeriesChart
          timeseriesChartData={timeseriesChartData}
          rPermission={rPermission}
          externalChartRefs={externalChartRefs}
          onChartRegistered={onChartRegistered}
        />
      </ProtectComponent>
    ),
  };

  return (
    <main className={`${className ? className : 'main-container'}`}>
      <Header
        accessible={accessible}
        timeseriesChartData={timeseriesChartData}
        retrieveMinMax={layout === 'Stacked'}
        openAddTaskModal={openAddTaskModal}
        fetchCov={layout === 'COV'}
        aggegateMethod={isTestDashboard ? 'mean' : 'last'}
        openAddNoteModal={() => openAddNoteModal(true)}
        layout={layout}
        isEditYAxisOpen={isEditYAxisOpen}
        setIsEditYAxisOpen={setIsEditYAxisOpen}
        setYAxisRefreshVersion={setYAxisRefreshVersion}
        disableTimeRangeSelector={isChartsDashboard ? true : false}
        rPermission={rPermission}
      />
      <hr className='horizontal-line' />
      {!accessible ? (
        <EmptyState message='You do not have access to this widget.' />
      ) : (
        <>
          {!isTestDashboard && layout === 'Stacked' && (
            <div className='edit-y-axis-button-container'>
              <ProtectAction canAccess={rPermission.canEditYAxis}>
                <button
                  analytics-id='edit-y-axis-btn'
                  className='time-range-button edit-y-axis-button'
                  onClick={() => setIsEditYAxisOpen(true)}
                >
                  <span className={`time-range-button__text`}>Edit Y axis</span>
                </button>
              </ProtectAction>
            </div>
          )}

          {isTestDashboard && layout === 'Split' ? (
            <ProtectComponent
              resource={R.widget(dashboardName, widgetName)}
              action='view'
              showInWidget={true}
              widgetMessageName='Split charts'
            >
              <div className='oilWaterGas-chart-container'>
                <SplitWellTestChart
                  splitTestChartData={timeseriesChartData}
                  widgetName={WIDGETS.WELL_TEST_SPLIT_CHART}
                  dashboardName={dashboardName}
                />
              </div>
            </ProtectComponent>
          ) : isTestDashboard ? (
            <ProtectComponent
              resource={R.widget(dashboardName, widgetName)}
              action='view'
              showInWidget={true}
              widgetMessageName='Split charts'
            >
              <div className='oilWaterGas-chart-container'>
                <ProtectComponent
                  resource={R.widget(dashboardName, widgetName)}
                  action='ogwtChartView'
                  graphViewHeader='Oil Gas Water Chart'
                >
                  <OilGasChart widgetName={WIDGETS.WELL_TEST_CHART} />
                </ProtectComponent>
                <ProtectComponent
                  resource={R.widget(dashboardName, widgetName)}
                  action='dailyAverageView'
                  graphViewHeader='Daily averages'
                >
                  <DailyAverageChart dailyAverageChartData={timeseriesChartData} rPermission={rPermission} />
                </ProtectComponent>
                <ProtectComponent
                  resource={R.widget(dashboardName, widgetName)}
                  action='analysisGraphView'
                  graphViewHeader='Analysis graphs'
                >
                  <AnalysisChart analysisChartPropsData={timeseriesChartData} rPermission={rPermission} />
                </ProtectComponent>
              </div>
            </ProtectComponent>
          ) : (
            <section className={`charts-stacked-section ${layout !== 'Stacked' ? 'charts-section' : ''}`}>
              {layoutComponents[layout] || (
                <TimeSeriesCharts
                  timeseriesChartData={timeseriesChartData}
                  rPermission={rPermission}
                  externalChartRefs={externalChartRefs}
                  onChartRegistered={onChartRegistered}
                />
              )}
            </section>
          )}
        </>
      )}
    </main>
  );
};

export default Dashboard;
